import type {
  ExportCSVPayload,
  ExportCSVResponse,
  FetchFiltersRefinersPayload,
  PageQuery,
  RefinerResponse,
} from '../components/DataGrid/types';
import { camelToSnake, snakeToCamel } from '../components/DataGrid/utils';
import { URL_PREFIX } from '../constants/api';
import type { TrackRollupRow } from '../types';
import { APIError } from './errors';

const arrayValues: (keyof TrackRollupRow)[] = [
  'isrcList',
  'gpidList',
  'majorLabelList',
  'presentationLabelList',
  'repertoireOwnerList',
  'deliveryTerritoryList',
  'trackRightsTerritoryList',
  'shortTitleList',
  'majorGenreDescList',
  'rollUpTitleList',
  'missedTerritoriesAlt',
  'appleTerritoryList',
  'missedAppleTerritoriesAlt',
  'spotifyTerritoryList',
  'missedSpotifyTerritoriesAlt',
];

const mapPageData = (
  data: Partial<TrackRollupRow>[],
): Partial<TrackRollupRow>[] => {
  return data.map((d) => {
    const mapped: { [key: string]: unknown } = {};
    Object.keys(d).forEach((k) => {
      const key = snakeToCamel(k.toLowerCase()) as keyof TrackRollupRow;
      const val = d[k as keyof typeof d];
      if (arrayValues.includes(key) && typeof val === 'string') {
        mapped[key] = JSON.parse(val);
        return;
      }
      mapped[key] = val;
    });

    return mapped as Partial<TrackRollupRow>;
  });
};

export type TrackRollupRowKey = keyof TrackRollupRow;
export type TrackRollupRowKeyWithPrefix =
  | TrackRollupRowKey
  | `-${TrackRollupRowKey}`;

interface PageDataResponse {
  data: Partial<TrackRollupRow>[];
  total: number;
}
export interface PageColl {
  data: Partial<TrackRollupRow>[];
  total?: number;
  nextPageToken?: string;
}

export const getPageData = async (
  query: PageQuery<TrackRollupRowKey, TrackRollupRowKeyWithPrefix>,
): Promise<PageColl> => {
  try {
    const defaultLimit = 100;
    const limit = query.limit ?? defaultLimit;
    const select = query.select?.map(camelToSnake);
    const order =
      query.order && query.order.length > 0
        ? query.order.map(camelToSnake)
        : [];

    const url = `${URL_PREFIX}/api/v1/track-rollup/`;
    const body = JSON.stringify({
      limit,
      select: [...(select ?? []), 'hash_key'],
      order,
      filter: query.filter,
      offset: query.offset,
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!response.ok) {
      throw new APIError('Failed to fetch page data', response.status);
    }

    const json = (await response.json()) as PageDataResponse;

    return {
      data: mapPageData(json.data),
      total: json.total,
      nextPageToken:
        json.data.length === limit
          ? `offset=${(query.offset ?? 0) + limit}`
          : '',
    };
  } catch (error) {
    console.warn('Error fetching track rollup page data:', error);
    throw error;
  }
};

export const exportCSV = async (
  gridData: ExportCSVPayload,
): Promise<ExportCSVResponse> => {
  try {
    const url = `${URL_PREFIX}/api/v1/track-rollup/export`;
    const body = JSON.stringify(gridData);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!response.ok) {
      throw new APIError('Failed to export CSV', response.status);
    }

    return await response.json();
  } catch (error) {
    console.warn('Error exporting CSV:', error);
    throw error;
  }
};

export const getFiltersRefiners = async (
  payload: FetchFiltersRefinersPayload,
): Promise<RefinerResponse> => {
  try {
    const url = `${URL_PREFIX}/api/v1/track-rollup/refiners`;
    const body = JSON.stringify(payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!response.ok) {
      throw new APIError('Failed to fetch refiners', response.status);
    }

    return await response.json();
  } catch (error) {
    console.warn('Error fetching refiners:', error);
    throw error;
  }
};
